import { useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2, Activity } from "lucide-react";
import { Sentry, initSentry } from "@/lib/sentry";
import { supabase } from "@/integrations/supabase/client";
import AdminOnly from "./AdminOnly";

type Status = "idle" | "running" | "success" | "partial" | "error";

interface ResultRow {
  label: string;
  ok: boolean;
  detail: string;
}

/**
 * Admin diagnostic that proves Sentry is wired up end-to-end:
 *  1. Confirms the browser SDK initialized (DSN reachable from edge).
 *  2. Captures a synthetic frontend exception and flushes it.
 *  3. Hits the `sentry-test` edge function which throws + reports server-side.
 *
 * Both events carry a unique `tag=` so the resulting Sentry issues are
 * easy to find in the dashboard.
 */
function SentryHealthCheckInner() {
  const [status, setStatus] = useState<Status>("idle");
  const [results, setResults] = useState<ResultRow[]>([]);
  const [tags, setTags] = useState<{ frontend?: string; edge?: string }>({});

  const run = async () => {
    setStatus("running");
    setResults([]);
    setTags({});
    const rows: ResultRow[] = [];
    const next: typeof tags = {};

    // 1. Make sure the browser SDK has initialized.
    try {
      await initSentry();
      const client = Sentry.getClient();
      if (client) {
        const dsn = client.getDsn();
        rows.push({
          label: "Browser SDK initialized",
          ok: true,
          detail: dsn ? `Reporting to ${dsn.host}` : "Reporting active",
        });
      } else {
        rows.push({
          label: "Browser SDK initialized",
          ok: false,
          detail: "No Sentry client — SENTRY_DSN may be missing from Cloud Secrets.",
        });
      }
    } catch (e) {
      rows.push({
        label: "Browser SDK initialized",
        ok: false,
        detail: e instanceof Error ? e.message : "Unknown error",
      });
    }

    // 2. Frontend synthetic exception.
    try {
      const tag = `fe-test-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      next.frontend = tag;
      const eventId = Sentry.captureException(
        new Error(`[SentryHealthCheck] synthetic frontend error • tag=${tag}`),
        { tags: { synthetic: "true", source: "admin-health-check", tag } },
      );
      // Best-effort flush so the user sees the issue in Sentry quickly.
      const flushed = await Sentry.flush(3000);
      rows.push({
        label: "Frontend error captured",
        ok: Boolean(eventId) && flushed,
        detail: eventId
          ? `Event ${eventId.slice(0, 8)}… ${flushed ? "flushed" : "queued (flush timed out)"} • tag=${tag}`
          : "captureException returned no event ID",
      });
    } catch (e) {
      rows.push({
        label: "Frontend error captured",
        ok: false,
        detail: e instanceof Error ? e.message : "Unknown error",
      });
    }

    // 3. Edge-function synthetic exception.
    try {
      const { data, error } = await supabase.functions.invoke("sentry-test");
      if (error) {
        rows.push({
          label: "Edge function error captured",
          ok: false,
          detail: error.message ?? "Edge function failed",
        });
      } else {
        const tag = (data as any)?.tag as string | undefined;
        if (tag) next.edge = tag;
        rows.push({
          label: "Edge function error captured",
          ok: Boolean((data as any)?.reported),
          detail: (data as any)?.reported
            ? `Reported & flushed server-side • tag=${tag ?? "n/a"}`
            : "Edge function ran but did not report",
        });
      }
    } catch (e) {
      rows.push({
        label: "Edge function error captured",
        ok: false,
        detail: e instanceof Error ? e.message : "Unknown error",
      });
    }

    setResults(rows);
    setTags(next);
    const okCount = rows.filter(r => r.ok).length;
    setStatus(okCount === rows.length ? "success" : okCount === 0 ? "error" : "partial");
  };

  const banner =
    status === "success"
      ? { color: "bg-green-50 text-green-800 border-green-200", icon: <CheckCircle2 className="h-4 w-4" />, text: "All checks passed — Sentry is reporting from this environment." }
      : status === "partial"
      ? { color: "bg-amber-50 text-amber-800 border-amber-200", icon: <AlertTriangle className="h-4 w-4" />, text: "Some checks failed — see details below." }
      : status === "error"
      ? { color: "bg-red-50 text-red-800 border-red-200", icon: <AlertTriangle className="h-4 w-4" />, text: "Sentry is not reporting from this environment." }
      : null;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-base font-bold text-[#1F3864] mb-1 flex items-center gap-2">
            <Activity className="h-4 w-4" /> Sentry Health Check
          </h3>
          <p className="text-sm text-gray-600">
            Sends a synthetic error from the browser and from an edge function,
            then verifies both were captured. Safe to run any time — events are
            tagged <code className="font-mono">synthetic=true</code>.
          </p>
        </div>
        <button
          onClick={run}
          disabled={status === "running"}
          className="shrink-0 px-4 py-2 rounded-md bg-[#1F3864] text-white text-sm font-semibold hover:bg-[#0F1E35] disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {status === "running" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Running…
            </>
          ) : (
            "Test Sentry"
          )}
        </button>
      </div>

      {banner && (
        <div className={`flex items-center gap-2 text-sm border rounded-md px-3 py-2 mb-3 ${banner.color}`}>
          {banner.icon}
          <span>{banner.text}</span>
        </div>
      )}

      {results.length > 0 && (
        <ul className="space-y-2">
          {results.map((r) => (
            <li key={r.label} className="flex items-start gap-2 text-sm">
              {r.ok ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
              )}
              <div>
                <div className="font-medium text-gray-800">{r.label}</div>
                <div className="text-gray-600 break-all">{r.detail}</div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {(tags.frontend || tags.edge) && status !== "running" && (
        <p className="mt-3 text-xs text-gray-500">
          Search Sentry for <code className="font-mono">tag:{tags.frontend ?? tags.edge}</code> to confirm the events arrived.
        </p>
      )}
    </div>
  );
}

export default function SentryHealthCheck() {
  return (
    <AdminOnly feature="Sentry Health Check">
      <SentryHealthCheckInner />
    </AdminOnly>
  );
}